import React from 'react';
import { Button as AntButton, Tooltip } from 'antd';
import type { ButtonProps as AntButtonProps } from 'antd';
import { FONT_SIZE, SPACING } from '../../constants/constants';
import { useThemeTokens } from '../../hooks/UseThemeTokens';

// ─── 타입 ─────────────────────────────────────────────────────────────────────

export type AppButtonVariant =
  | 'primary'      // 주요 액션 (수정가 조회, Naver 반영)
  | 'danger'       // 파괴적 액션 (삭제 확정)
  | 'ghost-danger' // 위험하지만 보조적 (선택 삭제, 유지)
  | 'default'      // 일반 액션 (엑셀 등록, 삭제 모드)
  | 'text'         // 크롬 없는 액션 (로그아웃)
  | 'link';        // 텍스트 링크 스타일 (화면 전환, AuthLink)

export type AppButtonSize = 'sm' | 'md' | 'lg';

export interface AppButtonProps
  extends Omit<AntButtonProps, 'type' | 'size' | 'danger' | 'variant'> {
  variant?: AppButtonVariant;
  size?: AppButtonSize;
  iconOnly?: boolean;
  disabledReason?: string;
}

// ─── 사이즈 맵 ────────────────────────────────────────────────────────────────

// height·padding은 버튼 고유 스펙이라 SPACING 사다리 대상이 아니다(CLAUDE.md §7 예외).
// fontSize는 FONT_SIZE 스케일과 겹치는 값(md=13→sm, lg=14→md)만 토큰으로 옮긴다 —
// sm=12는 스케일에 정확히 없는 값이라(가장 가까운 xs=11) 억지로 반올림하면 버튼 글자
// 크기가 미세하게 바뀌는 시각적 회귀 위험이 있어 레거시 값 그대로 둔다(2026-08-11).
const SIZE_MAP: Record<AppButtonSize, { antSize: AntButtonProps['size']; style: React.CSSProperties }> = {
  sm: { antSize: 'small',  style: { fontSize: 12, padding: '0 8px',  height: 26 } },
  md: { antSize: 'middle', style: { fontSize: FONT_SIZE.sm, padding: '0 14px', height: 32 } },
  lg: { antSize: 'large',  style: { fontSize: FONT_SIZE.md, padding: '0 18px', height: 44 } },
};

// ─── variant → 토큰 기반 스타일 ───────────────────────────────────────────────
//
// Ant 의 type="primary"/danger 가 끌어오는 기본 색을 쓰지 않고,
// 디자인 시스템 토큰으로 배경·테두리·글자색을 직접 지정한다.
//
function useVariantStyle(variant: AppButtonVariant): {
  antType: AntButtonProps['type'];
  style: React.CSSProperties;
} {
  const t = useThemeTokens();

  switch (variant) {
    case 'primary':
      return {
        antType: 'default',
        style: { background: t.accentSolid, borderColor: t.accentSolid, color: t.white },
      };
    case 'danger':
      return {
        antType: 'default',
        style: { background: t.dangerSolid, borderColor: t.dangerSolid, color: t.white },
      };
    case 'ghost-danger':
      return {
        antType: 'default',
        style: { background: 'transparent', borderColor: t.lineDanger, color: t.danger },
      };
    case 'default':
      return {
        antType: 'default',
        style: { background: t.bgSurface, borderColor: t.border, color: t.textPrimary },
      };
    case 'text':
      return {
        antType: 'text',
        style: { background: 'transparent', borderColor: 'transparent', color: t.textSecondary },
      };
    case 'link':
      return {
        antType: 'link',
        style: { background: 'transparent', borderColor: 'transparent', color: t.accent, padding: 0 },
      };
  }
}

// ─── AppButton ────────────────────────────────────────────────────────────────

/**
 * 프로젝트 공통 버튼.
 * 색상은 전부 디자인 시스템 토큰(useThemeTokens)에서 가져온다.
 *
 *   <AppButton variant="primary">수정 가격 조회</AppButton>
 *   <AppButton variant="danger" size="sm" icon={<DeleteOutlined />} iconOnly />
 *   <AppButton variant="primary" disabledReason="카드를 먼저 선택하세요" disabled>Naver 반영</AppButton>
 */
export const AppButton: React.FC<AppButtonProps> = ({
  variant = 'default',
  size = 'md',
  iconOnly = false,
  disabledReason,
  style,
  disabled,
  className,
  ...rest
}) => {
  const t = useThemeTokens();
  const { antSize, style: sizeStyle } = SIZE_MAP[size];
  const { antType, style: variantStyle } = useVariantStyle(variant);

  const iconOnlyStyle: React.CSSProperties = iconOnly
    ? { padding: 0, width: sizeStyle.height, minWidth: sizeStyle.height }
    : {};

  // 비활성 시 토큰 기반 흐린 색
  const disabledStyle: React.CSSProperties = disabled
    ? { background: t.bgRaised, borderColor: t.border, color: t.textDisabled }
    : {};

  const btn = (
    <AntButton
      type={antType}
      {...rest}
      // sm(26px) · md(32px)는 보이는 크기 그대로, 누르는 곳만 44px(index.css .app-hit, 감사 #693). lg 는 이미 44px.
      className={[size === 'lg' ? null : 'app-hit', className].filter(Boolean).join(' ') || undefined}
      size={antSize}
      disabled={disabled}
      style={{
        ...sizeStyle,
        ...variantStyle,
        ...iconOnlyStyle,
        ...disabledStyle,
        ...style,
      }}
    />
  );

  if (disabled && disabledReason) {
    return (
      <Tooltip
        title={disabledReason}
        placement="bottom"
        styles={{ container: { padding: `${SPACING.xxs}px ${SPACING.xs}px` } }}
      >
        <span style={{ display: 'inline-flex', cursor: 'not-allowed' }}>{btn}</span>
      </Tooltip>
    );
  }

  return btn;
};